import { MutableRefObject, useRef } from "react";

import { useSelector } from "react-redux";

import { MoneyTableColId } from "components/MoneyTable/columnDefinitions";

import { BillingDocumentColId } from "constants/columnDefinitions";

import { formatDateString, formatISO8601DateString } from "lib/timeFormats";

import {
  getActiveLivestockAgentDeployment,
  getBusinesses,
  getDefaultBillingSettings,
} from "selectors";
import {
  ColDef,
  ProcessCellForExportParams,
  ProcessHeaderForExportParams,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { EMPTY_OBJECT } from "lib";

interface ExtendedProcessCellForExportParams
  extends ProcessCellForExportParams {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ag-grid row data
  data: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ag-grid column definition
  colDef: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ag-grid row node
  node: any;
}

const processHeaderCallback = (params: ProcessHeaderForExportParams) => {
  const { column } = params;

  const columnDef: ColDef = column.getColDef();
  const parentColumnGroupDef: ColDef = column
    .getParent()
    .getProvidedColumnGroup()
    .getColGroupDef();

  if (parentColumnGroupDef.headerName) {
    return `${parentColumnGroupDef.headerName} ${columnDef.headerName}`;
  }
  return columnDef.headerName;
};

const processCellCallback = (cell: ExtendedProcessCellForExportParams) => {
  const { column, value } = cell;
  // If we're a date type, apply some sanity
  const columnDefinition: ColDef = column.getColDef();

  if (typeof columnDefinition.valueFormatter === "function") {
    return columnDefinition.valueFormatter(cell);
  }
  // If we're the actions column, make it blank
  if (column.getColDef().colId === BillingDocumentColId.ACTIONS) {
    return "";
  }
  return value;
};

export function useGetDownloadOutstandingSettlementReport() {
  const agGridRef: MutableRefObject<AgGridReact> = useRef<AgGridReact>();

  const activeLivestockAgentDeployment: Deployment =
    useSelector(getActiveLivestockAgentDeployment) ||
    (EMPTY_OBJECT as Deployment);

  const billingSettings = useSelector(getDefaultBillingSettings);

  const deploymentSettings: DeploymentSettings =
    activeLivestockAgentDeployment.deploymentSettings ||
    (EMPTY_OBJECT as DeploymentSettings);

  const businesses: BusinessByIdLookup =
    useSelector(getBusinesses) || EMPTY_OBJECT;

  const provider: Business =
    (billingSettings &&
      businesses[billingSettings.debtorInsurance?.providerId]) ||
    (EMPTY_OBJECT as Business);

  // Pull an aggrid api reference in, so we can create some export magic.
  function onGridReady(agGrid: AgGridReact) {
    agGridRef.current = agGrid;
  }

  const handleDownloadOutstandingSettlementReport = () => {
    const agGrid: AgGridReact = agGridRef.current;

    // Grab the columns we're showing, so we can count them.
    const currentColumnState = agGrid.columnApi.getColumnState();
    // Explicitly IGNORE the field used for filtering.  This should give the user the ability to add other columns
    // (EG ABN?) if required.
    const columnKeys = currentColumnState
      .filter(
        colState =>
          !colState.hide &&
          colState.colId !==
            MoneyTableColId.SHOW_ON_OUTSTANDING_SETTLEMENT_REPORT,
      )
      .map(colState => colState.colId);

    const numberOfColumns = columnKeys.length;
    const blankFooterRows = {
      mergeAcross: numberOfColumns - 3,
      data: {
        value: "",
        type: "String" as const,
      },
    };

    const { physicalAddress } = deploymentSettings;
    const { postalAddress } = billingSettings;

    const address = postalAddress || physicalAddress;
    const addressLine1 = address
      ? `${address.streetNumber} ${address.route}`
      : "";
    const addressLine2 = address
      ? `${address.locality} ${address.state} ${address.postalCode}`
      : "";

    const prependContent = [
      {
        cells: [
          {
            mergeAcross: numberOfColumns - 3,
            data: {
              value: billingSettings.companyName,
              type: "String" as const,
            },
            styleId: "bold",
          },
          {
            mergeAcross: 1,
            data: {
              value: `TEL ${billingSettings.phoneNumber}`,
              type: "String" as const,
            },
          },
        ],
      },

      {
        cells: [
          {
            mergeAcross: 1,
            data: {
              value: "WEEKLY REPORT",
              type: "String" as const,
            },
          },
          {
            mergeAcross: numberOfColumns - 3,
            data: {
              value: provider.tradingName || provider.name || "",
              type: "String" as const,
            },
          },
        ],
      },
      {
        cells: [
          {
            mergeAcross: 1,
            data: {
              value: "",
              type: "String" as const,
            },
          },

          {
            mergeAcross: numberOfColumns - 3,
            data: {
              value: "OUTSTANDING SETTLEMENT REPORT",
              type: "String" as const,
            },
          },
        ],
      },

      {
        cells: [
          {
            mergeAcross: 1,
            data: {
              value: "Paid within the last 7 days or outstanding as of",
              type: "String" as const,
            },
          },
          {
            mergeAcross: numberOfColumns - 3,
            data: {
              value: formatDateString(new Date()),
              type: "String" as const,
            },
          },
        ],
      },

      {
        cells: [
          {
            mergeAcross: 3,
            data: {
              value: `COMPANY - ${billingSettings.companyName}`,
              type: "String" as const,
            },
          },
          {
            mergeAcross: numberOfColumns - 5,
            data: {
              value: `Email: ${provider.primaryContactEmail || provider.emailRecipients?.filter(er => er.email)?.[0]?.email || ""}`,
              type: "String" as const,
            },
          },
        ],
      },
      {
        cells: [],
      },
    ];

    const appendContent = [
      {
        cells: [
          blankFooterRows,
          {
            type: "String" as const,
            mergeAcross: 1,
            data: {
              value: billingSettings.companyName,
              type: "String" as const,
            },
            styleId: "horizontal-center",
          },
        ],
      },

      {
        cells: [
          blankFooterRows,
          {
            type: "String" as const,
            mergeAcross: 1,
            data: {
              value: addressLine1,
              type: "String" as const,
            },
            styleId: "horizontal-center",
          },
        ],
      },
      {
        cells: [
          blankFooterRows,
          {
            type: "String" as const,
            mergeAcross: 1,
            data: {
              value: addressLine2,
              type: "String" as const,
            },
            styleId: "horizontal-center",
          },
        ],
      },
    ];

    agGrid.api.exportDataAsExcel({
      appendContent,
      prependContent,
      processHeaderCallback,
      processCellCallback,
      skipColumnGroupHeaders: true,
      fileName: `Outstanding Settlement Report - ${formatISO8601DateString(
        new Date(),
      )}.xlsx`,
      columnKeys,
    });
  };

  return [onGridReady, handleDownloadOutstandingSettlementReport];
}
